import { Modal, ModalProps } from '@/modules/common/components/Modal';
import {
  ApplicationValidationReviewer,
  ApplicationValidationType,
  IApplicationWithOrderAndCourse,
} from '@/types/application';
import { yupResolver } from '@hookform/resolvers/yup';
import { Check, Close } from '@mui/icons-material';
import { Button, FormHelperText, Tooltip } from '@mui/material';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { ApplicationDetailsRow } from './ApplicationDetailsRow';
import { CommentBox } from './CommentBox';
import { CourseCard } from './CourseCard';
import { ReviewModalState } from './ReviewModal.types';

type EmployerLearningBudgetReviewModalProps = Omit<
  ModalProps,
  'title' | 'subtitle' | 'children' | 'onClose'
> & {
  application: IApplicationWithOrderAndCourse;
  onClose: (submittedReview?: Partial<ReviewModalState>) => void;
};

type FormValues = {
  [ApplicationValidationType.LearningBudget]: boolean;
  comments?: string;
};

export function EmployerLearningBudgetReviewModal({
  application,
  onClose,
  ...props
}: EmployerLearningBudgetReviewModalProps) {
  const {
    learningBudget: applicationLearningBudgetAmount,
    order: {
      userPaymentAmount: orderUserPaymentAmount,
      totalAmount: orderTotalAmount,
    } = {},
    user: {
      profile: { learningBudgetGrossBalance: remainingLearningBudget } = {},
    } = {},
    validations,
  } = application;

  const employerLearningBudgetValidation = validations?.find(
    validation =>
      validation.name === ApplicationValidationType.LearningBudget &&
      validation.reviews.some(
        review => review.reviewer === ApplicationValidationReviewer.Employer,
      ),
  );

  const formMethods = useForm<FormValues>({
    defaultValues: {
      [ApplicationValidationType.LearningBudget]: false,
      comments: '',
    },
    resolver: yupResolver(
      yup
        .object()
        .shape({
          [ApplicationValidationType.LearningBudget]: yup
            .boolean()
            .required()
            .nonNullable(),
          comments: yup.string(),
        })
        .test(() => {
          if (
            !employerLearningBudgetValidation ||
            applicationLearningBudgetAmount?.amount === undefined ||
            !Number.isFinite(applicationLearningBudgetAmount?.amount) ||
            remainingLearningBudget === undefined ||
            !Number.isFinite(remainingLearningBudget)
          ) {
            throw new yup.ValidationError(
              'Invalid application data. Please try again later.',
              undefined,
              ApplicationValidationType.LearningBudget,
            );
          }

          return true;
        }),
    ),
  });
  const {
    formState,
    setValue,
    trigger,
    formState: { errors },
  } = formMethods;

  const onSubmit = (data: FormValues) => {
    onClose({
      [ApplicationValidationType.LearningBudget]:
        data[ApplicationValidationType.LearningBudget],
      comments: data.comments,
    });
  };

  useEffect(() => {
    trigger(ApplicationValidationType.LearningBudget);
  }, [trigger, application]);

  const totalAmount = (orderUserPaymentAmount?.amount || orderTotalAmount) ?? 0;
  const selectedCohort = application.course?.cohorts?.find(
    ({ _id }) => application.cohort === _id,
  );

  return (
    <Modal
      onClose={() => onClose(undefined)}
      showCloseButton
      title={
        <>
          Please review <span className="font-semibold">1 learning budget</span>{' '}
          request.
        </>
      }
      classes={{
        paper: 'max-w-3xl',
      }}
      subTitle="By clicking 'Approve,' the learning budget will be automatically integrated with the application. If 'rejected', the user will receive an email with further details."
      {...props}
      actions={
        <>
          <Button
            variant="outlined"
            type="submit"
            onClick={() =>
              setValue(ApplicationValidationType.LearningBudget, false)
            }
            endIcon={<Close />}
            disabled={formState.isSubmitting || !formState.isValid}
            form="lb-review-form"
          >
            Reject
          </Button>
          <Tooltip
            title={
              formState.errors[ApplicationValidationType.LearningBudget]
                ?.message
            }
          >
            <span>
              <Button
                variant="contained"
                type="submit"
                endIcon={<Check />}
                loading={formState.isSubmitting}
                onClick={() => {
                  setValue(ApplicationValidationType.LearningBudget, true);
                }}
                form="lb-review-form"
                disabled={
                  !!formState.errors[ApplicationValidationType.LearningBudget]
                    ?.message
                }
              >
                Approve
              </Button>
            </span>
          </Tooltip>
        </>
      }
    >
      <FormProvider {...formMethods}>
        <form
          onSubmit={formMethods.handleSubmit(onSubmit)}
          className="flex flex-col gap-4"
          id="lb-review-form"
        >
          <CourseCard
            course={application.course}
            selectedCohort={selectedCohort}
          />
          <ApplicationDetailsRow
            user={application.user}
            applicationLearningBudgetAmount={applicationLearningBudgetAmount}
            remainingLearningBudget={remainingLearningBudget}
            totalAmount={totalAmount}
            orderUserPaymentAmount={orderUserPaymentAmount}
          />
          <CommentBox className="-mt-2" />
          <FormHelperText
            error={!!errors[ApplicationValidationType.LearningBudget]}
          >
            {typeof errors[ApplicationValidationType.LearningBudget]
              ?.message === 'string'
              ? errors[ApplicationValidationType.LearningBudget]?.message
              : undefined}
          </FormHelperText>
        </form>
      </FormProvider>
    </Modal>
  );
}
